import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import StatCard from '../components/StatCard'
import SurveyReportsModal from '../components/SurveyReportsModal'
import { dashboardAPI, referenceAPI, surveyAPI } from '../services/api'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js'
import { Line, Doughnut, Pie } from 'react-chartjs-2'

// Date formatting utilities for DD/MM/YYYY format
const formatDateToDisplay = (dateString) => {
  if (!dateString) return ''
  const [year, month, day] = dateString.split('-')
  return `${day}/${month}/${year}`
}

const formatDateToAPI = (displayString) => {
  if (!displayString) return ''
  // Handle DD/MM/YYYY format and convert to YYYY-MM-DD
  const parts = displayString.split('/')
  if (parts.length === 3) {
    const [day, month, year] = parts
    return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`
  }
  return displayString // Return as-is if already in correct format
}

const getCurrentDateString = () => {
  const today = new Date()
  const year = today.getFullYear()
  const month = String(today.getMonth() + 1).padStart(2, '0')
  const day = String(today.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
)

const Dashboard = () => {
  const { isSuperuser, userGroups, isFinanceManager } = useAuth()
  // Direct group-based and user-based access control
  const isAdmin = isSuperuser || userGroups.includes('Admin')
  const hasFullAccess = isSuperuser || isAdmin || isFinanceManager
  const isReadOnlyClient = userGroups.includes('ReadOnlyClient')
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState(null)
  const [filters, setFilters] = useState({
    start_date: '',
    end_date: '',
    skip_date_filter: true, // Default to showing all data
    client: '',
    survey_type: '',
    country: '',
    port: '',
    surveyor: '',
    payment_status: '',
  })
  const [displayFilters, setDisplayFilters] = useState({
    start_date: '',
    end_date: '',
  })
  const [activeFilterField, setActiveFilterField] = useState('')
  const [selectedFilters, setSelectedFilters] = useState({
    client: [],
    survey_type: [],
    country: [],
    port: [],
    surveyor: [],
    payment_status: [],
  })
  const [tempSelections, setTempSelections] = useState({
    client: [],
    survey_type: [],
    country: [],
    port: [],
    surveyor: [],
    payment_status: [],
  })
  const [filterOptions, setFilterOptions] = useState({
    clients: [],
    surveyTypes: [],
    countries: [],
    ports: [],
    surveyors: [],
    paymentStatuses: ['Unpaid'],
  })
  const [showFilterFrame, setShowFilterFrame] = useState(true)
  const [surveys, setSurveys] = useState([])
  const [surveysLoading, setSurveysLoading] = useState(false)
  const [surveyPage, setSurveyPage] = useState(1)
  const [surveyTotalPages, setSurveyTotalPages] = useState(1)
  const [surveyTotalCount, setSurveyTotalCount] = useState(0)
  const [reportsModalRef, setReportsModalRef] = useState(null)
  const [showSOAModal, setShowSOAModal] = useState(false)
  const [soaData, setSoaData] = useState([])
  const [soaLoading, setSoaLoading] = useState(false)

  useEffect(() => {
    // Only Superuser, Admin, Manager, Finance Manager, Staff, or ReadOnlyClient can access dashboard
    const allowedGroups = ['Admin', 'Manager', 'Finance Manager', 'Staff', 'ReadOnlyClient']
    const hasAccess = isSuperuser || userGroups.some(group => allowedGroups.includes(group))
    
    if (!hasAccess) {
      window.location.href = '/operations'
      return
    }
    loadFilterOptions()
    // Fetch all data on initial load (skip_date_filter is true by default)
    fetchStats()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuperuser, userGroups])

  // Fetch stats and surveys when filters change
  useEffect(() => {
    if (isSuperuser || userGroups.some(group => ['Admin', 'Manager', 'Finance Manager', 'Staff', 'ReadOnlyClient'].includes(group))) {
      fetchStats()
      fetchSurveys(1) // Also refetch surveys when filters change
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, isSuperuser, userGroups])

  const loadFilterOptions = async () => {
    try {
      const promises = [
        referenceAPI.getClients(),
        referenceAPI.getSurveyTypes(),
        referenceAPI.getCountries(),
      ]
      
      // Load surveyors for all users
      promises.push(referenceAPI.getSurveyorsByType())
      
      const results = await Promise.all(promises)
      const [clientsRes, typesRes, countriesRes, surveyorsRes] = results
      
      setFilterOptions(prev => ({
        ...prev,
        clients: clientsRes.data.clients || [],
        surveyTypes: typesRes.data.survey_types || [],
        countries: countriesRes.data.countries || [],
        surveyors: (surveyorsRes?.data?.surveyors || []).map(s => s.code || s.name), // Extract codes
      }))
    } catch (error) {
      console.error('Error loading filter options:', error)
    }
  }

  const loadPorts = async (country) => {
    if (!country) {
      setFilterOptions(prev => ({ ...prev, ports: [] }))
      return
    }
    try {
      const response = await referenceAPI.getPorts(country)
      setFilterOptions(prev => ({
        ...prev,
        ports: response.data.ports || [],
      }))
    } catch (error) {
      console.error('Error loading ports:', error)
    }
  }

  useEffect(() => {
    if (filters.country) {
      // If multiple countries selected, use the first one for port loading
      const countryForPorts = filters.country.split(',')[0].trim()
      loadPorts(countryForPorts)
    } else {
      setFilterOptions(prev => ({ ...prev, ports: [] }))
      setSelectedFilters(prev => ({ ...prev, port: [] }))
      setFilters(prev => ({ ...prev, port: '' }))
    }
  }, [filters.country])

  // Close dropdown when clicking outside (optional - can keep field selected)
  // Users can manually deselect by choosing empty option

  const fetchSurveys = async (page = surveyPage) => {
    setSurveysLoading(true)
    try {
      const params = {
        page: page,
      }
      
      // Handle date filters - same as Dashboard stats
      if (filters.skip_date_filter) {
        params.skip_date_filter = 'true'
      } else {
        if (filters.start_date) params.start_date = filters.start_date
        if (filters.end_date) params.end_date = filters.end_date
      }
      
      // Add other filters only if they have values - same as Dashboard stats
      if (filters.client) params.client = filters.client
      if (filters.survey_type) params.survey_type = filters.survey_type
      if (filters.country) params.country = filters.country
      if (filters.port) params.port = filters.port
      if (filters.surveyor) params.surveyor = filters.surveyor
      if (filters.payment_status) params.payment_status = filters.payment_status

      // Fetch ALL pages to find all surveys with attendance dates
      let allResults = []
      let currentPage = 1
      let totalPages = 1
      
      do {
        const pageResponse = await surveyAPI.list({ ...params, page: currentPage })
        if (pageResponse.data.results) {
          allResults = [...allResults, ...pageResponse.data.results]
          totalPages = pageResponse.data.total_pages || 1
        }
        currentPage++
      } while (currentPage <= totalPages)
      
      let filteredResults = allResults
      
      // Apply client-side filtering since JNJ backend doesn't support it like KBL
      if (filters.client || filters.survey_type || filters.country || filters.port || filters.surveyor || filters.payment_status || true) {
        filteredResults = filteredResults.filter(survey => {
          // Filter by attendance date - Dashboard stats only count surveys with attendance dates
          if (!survey.attendance_date) {
            return false
          }
          
          // Client filter
          if (filters.client && !survey.client_name?.toLowerCase().includes(filters.client.toLowerCase())) {
            return false
          }
          
          // Survey type filter
          if (filters.survey_type && !survey.survey_type?.toLowerCase().includes(filters.survey_type.toLowerCase())) {
            return false
          }
          
          // Country filter
          if (filters.country && !survey.survey_country?.toLowerCase().includes(filters.country.toLowerCase())) {
            return false
          }
          
          // Port filter
          if (filters.port && !survey.survey_port?.toLowerCase().includes(filters.port.toLowerCase())) {
            return false
          }
          
          // Surveyor filter
          if (filters.surveyor && !survey.surveyor_code?.toLowerCase().includes(filters.surveyor.toLowerCase())) {
            return false
          }
          
          // Payment status filter - Unpaid means no Received Payment Date
          if (filters.payment_status && filters.payment_status.includes('Unpaid') && survey.received_payment_date) {
            return false
          }
          
          return true
        })
      }
      
      if (allResults.length > 0) {
        setSurveys(filteredResults)
        setSurveyTotalPages(1) // For filtered results, use single page
        setSurveyTotalCount(filteredResults.length)
      }
    } catch (error) {
      console.error('Error fetching surveys:', error)
    } finally {
      setSurveysLoading(false)
    }
  }

  const fetchStats = async () => {
    setLoading(true)
    try {
      const params = new URLSearchParams()
      
      // Handle date filters
      if (filters.skip_date_filter) {
        // Skip date filter - show all dates
        params.append('skip_date_filter', 'true')
      } else {
        // Apply date filters if skip_date_filter is false
        if (filters.start_date) params.append('start_date', filters.start_date)
        if (filters.end_date) params.append('end_date', filters.end_date)
      }
      
      // Add other filters only if they have values
      if (filters.client) params.append('client', filters.client)
      if (filters.survey_type) params.append('survey_type', filters.survey_type)
      if (filters.country) params.append('country', filters.country)
      if (filters.port) params.append('port', filters.port)
      if (filters.surveyor) params.append('surveyor', filters.surveyor)
      if (filters.payment_status) params.append('payment_status', filters.payment_status)

      const response = await dashboardAPI.getStats(params)
      if (response.data.status === 'success') {
        setStats(response.data.data)
      }
      
      // Fetch surveys with the same filters (reset to page 1)
      setSurveyPage(1)
      await fetchSurveys(1)
    } catch (error) {
      console.error('Error fetching dashboard stats:', error)
    } finally {
      setLoading(false)
    }
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => {
      const newFilters = { ...prev, [key]: value }
      // Clear port if country changes
      if (key === 'country' && value !== prev.country) {
        newFilters.port = ''
        setSelectedFilters(prev => ({ ...prev, port: [] }))
      }
      // When dates are manually set, disable skip_date_filter
      if (key === 'start_date' || key === 'end_date') {
        newFilters.skip_date_filter = false
        // Update display filters for date fields
        if (key === 'start_date') {
          setDisplayFilters(prev => ({ ...prev, start_date: formatDateToDisplay(value) }))
        } else if (key === 'end_date') {
          setDisplayFilters(prev => ({ ...prev, end_date: formatDateToDisplay(value) }))
        }
      }
      return newFilters
    })
  }

  const handleDisplayDateChange = (key, displayValue) => {
    // Update display filter
    setDisplayFilters(prev => ({ ...prev, [key]: displayValue }))
    
    // Convert to API format and update actual filter
    const apiValue = formatDateToAPI(displayValue)
    setFilters(prev => {
      const newFilters = { ...prev, [key]: apiValue, skip_date_filter: false }
      return newFilters
    })
  }

  const toggleTempSelection = (field, option) => {
    setTempSelections(prev => {
      const current = prev[field] || []
      const updated = current.includes(option)
        ? current.filter(item => item !== option)
        : [...current, option]
      return { ...prev, [field]: updated }
    })
  }

  const handleAddToFilter = () => {
    if (!activeFilterField) return
    
    // Add temporary selections to actual filters
    setSelectedFilters(prev => {
      const newFilters = { ...prev }
      if (activeFilterField !== 'date') {
        newFilters[activeFilterField] = [...tempSelections[activeFilterField]]
        setFilters(prevFilters => ({
          ...prevFilters,
          [activeFilterField]: tempSelections[activeFilterField].length > 0 
            ? tempSelections[activeFilterField].join(',') 
            : ''
        }))
      }
      return newFilters
    })
    
    // Close the options window - useEffect will trigger refetch automatically
    setActiveFilterField('')
  }

  const toggleFilterOption = (field, option) => {
    setSelectedFilters(prev => {
      const current = prev[field] || []
      const updated = current.includes(option)
        ? current.filter(item => item !== option)
        : [...current, option]
      
      // Update filters object with comma-separated values
      setFilters(prevFilters => ({
        ...prevFilters,
        [field]: updated.length > 0 ? updated.join(',') : ''
      }))
      
      return { ...prev, [field]: updated }
    })
  }

  const handleApplyFilters = (e) => {
    e.preventDefault()
    setActiveFilterField('')
    setSurveyPage(1) // Reset to first page when filters are applied
    fetchStats()
  }

  const handleClearFilters = () => {
    setFilters({
      start_date: '',
      end_date: '',
      skip_date_filter: true, // Reset to show all data
      client: '',
      survey_type: '',
      country: '',
      port: '',
      surveyor: '',
      payment_status: '',
    })
    setDisplayFilters({
      start_date: '',
      end_date: '',
    })
    setSelectedFilters({
      client: [],
      survey_type: [],
      country: [],
      port: [],
      surveyor: [],
      payment_status: [],
    })
    setTempSelections({
      client: [],
      survey_type: [],
      country: [],
      port: [],
      surveyor: [],
      payment_status: [],
    })
    setActiveFilterField('')
    // Fetch stats after clearing to show all data
    setTimeout(() => fetchStats(), 100)
  }

  const handleExportExcel = async () => {
    try {
      // Build params object from current filters (same as fetchStats)
      const params = {}
      
      // Handle date filters
      if (filters.skip_date_filter) {
        params.skip_date_filter = 'true'
      } else {
        if (filters.start_date) params.start_date = filters.start_date
        if (filters.end_date) params.end_date = filters.end_date
      }
      
      // Add other filters only if they have values
      if (filters.client) params.client = filters.client
      if (filters.survey_type) params.survey_type = filters.survey_type
      if (filters.country) params.country = filters.country
      if (filters.port) params.port = filters.port
      if (filters.surveyor) params.surveyor = filters.surveyor
      if (filters.payment_status) params.payment_status = filters.payment_status

      console.log('Exporting with params:', params)

      // Call export API
      const response = await surveyAPI.export(params)
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Try to extract filename from Content-Disposition header, otherwise generate it
      let filename = 'JNJ_Surveys.xlsx'
      // Axios normalizes headers to lowercase
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '')
        }
      } else {
        // Fallback: generate filename based on user role and date
        const today = new Date()
        const year = today.getFullYear()
        const month = String(today.getMonth() + 1).padStart(2, '0')
        const day = String(today.getDate()).padStart(2, '0')
        const todayStr = `${year}${month}${day}`
        filename = hasFullAccess 
          ? `JNJ_Surveys_admin_${todayStr}.xlsx`
          : `JNJ_Surveys_staff_${todayStr}.xlsx`
      }
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting to Excel:', error)
      alert('Failed to export surveys to Excel. Please try again.')
    }
  }

  const handleGenerateSOA = async () => {
    setSoaLoading(true)
    try {
      // Build params object from current filters (same as fetchStats)
      const params = {}
      
      // Handle date filters
      if (filters.skip_date_filter) {
        params.skip_date_filter = 'true'
      } else {
        if (filters.start_date) params.start_date = filters.start_date
        if (filters.end_date) params.end_date = filters.end_date
      }
      
      // Add other filters only if they have values
      if (filters.client) params.client = filters.client
      if (filters.survey_type) params.survey_type = filters.survey_type
      if (filters.country) params.country = filters.country
      if (filters.port) params.port = filters.port
      if (filters.surveyor) params.surveyor = filters.surveyor
      if (filters.payment_status) params.payment_status = filters.payment_status

      // Call SoA generation API
      const response = await dashboardAPI.generateSOA(params)
      
      if (response.data.status === 'success') {
        setSoaData(response.data.data)
        setShowSOAModal(true)
      } else {
        alert('Failed to generate Statement of Account. Please try again.')
      }
    } catch (error) {
      console.error('Error generating SoA:', error)
      alert('Failed to generate Statement of Account. Please try again.')
    } finally {
      setSoaLoading(false)
    }
  }

  const handleExportSOA = async () => {
    try {
      // Build params object from current filters (same as generateSOA)
      const params = {}
      
      // Handle date filters
      if (filters.skip_date_filter) {
        params.skip_date_filter = 'true'
      } else {
        if (filters.start_date) params.start_date = filters.start_date
        if (filters.end_date) params.end_date = filters.end_date
      }
      
      // Add other filters only if they have values
      if (filters.client) params.client = filters.client
      if (filters.survey_type) params.survey_type = filters.survey_type
      if (filters.country) params.country = filters.country
      if (filters.port) params.port = filters.port
      if (filters.surveyor) params.surveyor = filters.surveyor
      if (filters.payment_status) params.payment_status = filters.payment_status

      // Call SoA export API
      const response = await dashboardAPI.exportSOA(params)
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      
      // Generate filename for SoA
      const today = new Date()
      const year = today.getFullYear()
      const month = String(today.getMonth() + 1).padStart(2, '0')
      const day = String(today.getDate()).padStart(2, '0')
      const todayStr = `${year}${month}${day}`
      let filename = `JNJ_SoA_${todayStr}.xlsx`
      
      // Try to extract filename from Content-Disposition header
      const contentDisposition = response.headers['content-disposition'] || response.headers['Content-Disposition']
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/)
        if (filenameMatch && filenameMatch[1]) {
          filename = filenameMatch[1].replace(/['"]/g, '')
        }
      }
      
      link.setAttribute('download', filename)
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch (error) {
      console.error('Error exporting SoA to Excel:', error)
      alert('Failed to export Statement of Account to Excel. Please try again.')
    }
  }

  const handleQuickDateFilter = (preset) => {
    const today = new Date()
    let startDate = ''
    let endDate = getCurrentDateString()
    
    switch(preset) {
      case 'all':
        setFilters(prev => ({ ...prev, skip_date_filter: true, start_date: '', end_date: '' }))
        setDisplayFilters(prev => ({ ...prev, start_date: '', end_date: '' }))
        return
      case 'this-month':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1)
        const startMonth = startDate.getFullYear()
        const startMonthNum = String(startDate.getMonth() + 1).padStart(2, '0')
        const startMonthDay = String(startDate.getDate()).padStart(2, '0')
        startDate = `${startMonth}-${startMonthNum}-${startMonthDay}`
        break
      case 'this-quarter':
        const quarter = Math.floor(today.getMonth() / 3)
        startDate = new Date(today.getFullYear(), quarter * 3, 1)
        const startQuarter = startDate.getFullYear()
        const startQuarterNum = String(startDate.getMonth() + 1).padStart(2, '0')
        const startQuarterDay = String(startDate.getDate()).padStart(2, '0')
        startDate = `${startQuarter}-${startQuarterNum}-${startQuarterDay}`
        break
      case 'this-year':
        startDate = new Date(today.getFullYear(), 0, 1)
        const startYear = startDate.getFullYear()
        const startYearNum = String(startDate.getMonth() + 1).padStart(2, '0')
        const startYearDay = String(startDate.getDate()).padStart(2, '0')
        startDate = `${startYear}-${startYearNum}-${startYearDay}`
        break
      case 'last-7-days':
        const last7 = new Date(today)
        last7.setDate(today.getDate() - 7)
        const l7 = last7.getFullYear()
        const l7Month = String(last7.getMonth() + 1).padStart(2, '0')
        const l7Day = String(last7.getDate()).padStart(2, '0')
        startDate = `${l7}-${l7Month}-${l7Day}`
        break
      case 'last-30-days':
        const last30 = new Date(today)
        last30.setDate(today.getDate() - 30)
        const l30 = last30.getFullYear()
        const l30Month = String(last30.getMonth() + 1).padStart(2, '0')
        const l30Day = String(last30.getDate()).padStart(2, '0')
        startDate = `${l30}-${l30Month}-${l30Day}`
        break
      case 'last-3-months':
        const last3Months = new Date(today)
        last3Months.setMonth(today.getMonth() - 3)
        const l3 = last3Months.getFullYear()
        const l3Month = String(last3Months.getMonth() + 1).padStart(2, '0')
        const l3Day = String(last3Months.getDate()).padStart(2, '0')
        startDate = `${l3}-${l3Month}-${l3Day}`
        break
      case 'last-year':
        const lyStart = new Date(today.getFullYear() - 1, 0, 1)
        const lyEnd = new Date(today.getFullYear() - 1, 11, 31)
        const lyStartYear = lyStart.getFullYear()
        const lyStartMonth = String(lyStart.getMonth() + 1).padStart(2, '0')
        const lyStartDay = String(lyStart.getDate()).padStart(2, '0')
        const lyEndYear = lyEnd.getFullYear()
        const lyEndMonth = String(lyEnd.getMonth() + 1).padStart(2, '0')
        const lyEndDay = String(lyEnd.getDate()).padStart(2, '0')
        startDate = `${lyStartYear}-${lyStartMonth}-${lyStartDay}`
        endDate = `${lyEndYear}-${lyEndMonth}-${lyEndDay}`
        break
      default:
        return
    }
    
    setFilters(prev => ({ 
      ...prev, 
      start_date: startDate, 
      end_date: endDate,
      skip_date_filter: false 
    }))
    setDisplayFilters(prev => ({
      ...prev,
      start_date: formatDateToDisplay(startDate),
      end_date: formatDateToDisplay(endDate)
    }))
  }

  const handleFieldChange = (field) => {
    const newField = field === activeFilterField ? '' : field
    setActiveFilterField(newField)
    
    // Enable date inputs when Date filter is selected
    if (newField === 'date') {
      setFilters(prev => ({ ...prev, skip_date_filter: false }))
    }
    
    // Initialize temporary selections with current filter values when opening a field
    if (newField && newField !== 'date') {
      const currentValues = selectedFilters[newField] || []
      setTempSelections(prev => ({ ...prev, [newField]: [...currentValues] }))
    }
    
    // Load ports if country is selected and port field is chosen
    if (newField === 'port' && filters.country) {
      const countryForPorts = filters.country.split(',')[0].trim()
      loadPorts(countryForPorts)
    }
  }

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-altus-primary"></div>
      </div>
    )
  }

  const trendData = stats?.surveys_by_month || []
  const typeData = stats?.surveys_by_type || []
  const fuelTrendData = trendData.map(d => ({ month: d.month, value: d.fuel_mt || 0 }))
  const savingTrendData = trendData.map(d => ({ month: d.month, value: d.saving || 0 }))

  return (
    <div className="page-container">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold"><span className="text-premium">JNJ Survey Dashboard</span></h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">Survey statistics, client activity and financial overview</p>
        </div>
        <div className="flex items-center gap-2">
          {(
            <button
              onClick={() => setShowFilterFrame(!showFilterFrame)}
              className="p-2 rounded-lg text-white transition-all duration-200 hover:scale-110 active:scale-95 shadow-md hover:shadow-lg"
              style={{ backgroundColor: '#0a5fe0' }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#0a4fd4'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#0a5fe0'}
              title="Toggle Filters"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </button>
          )}
          <button
            onClick={() => setShowFilterFrame(!showFilterFrame)}
            className="p-2 rounded-lg text-white transition-all duration-200 hover:scale-110 active:scale-95 shadow-md hover:shadow-lg"
            style={{ backgroundColor: '#0a5fe0' }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#0a4fd4'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#0a5fe0'}
            title="Toggle Filters"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Professional Filter Frame - 4 Column Layout */}
      {showFilterFrame && (
        <div className="bg-white rounded-xl border border-[#d9e2f1] shadow-[0_1px_6px_rgba(1,109,247,0.12)] overflow-hidden mb-6">
          <div className="table-head-premium px-5 py-2">
            <h3 className="text-sm font-semibold text-white flex items-center justify-between">
              <div className="flex items-center gap-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filter Options
              </div>
              <button
                onClick={() => setShowFilterFrame(false)}
                className="p-1 rounded-md hover:bg-white/20 transition-colors"
                title="Close Filters"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </h3>
          </div>
        
        <form onSubmit={handleApplyFilters} className="p-5">
          {/* 3 Column Layout (Filter Field, Options, Quick Date Filters) - 40% 40% 20% */}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            {/* Column 1: Filter Fields - 40% width */}
            <div className="md:col-span-2">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Filter Field</label>
              <select
                value={activeFilterField}
                onChange={(e) => handleFieldChange(e.target.value)}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-white mb-4"
              >
                <option value="">🔍 Select Filter Field</option>
                <option value="date">📅 Date</option>
                <option value="client">👤 Client</option>
                <option value="survey_type">📋 Survey Type</option>
                <option value="country">🌍 Country</option>
                <option value="port">⚓ Port</option>
                {!isReadOnlyClient && <option value="surveyor">👨‍💼 Surveyor</option>}
                <option value="payment_status">💰 Payment Status</option>
              </select>
            </div>

            {/* Column 2: Filter Options - Always rendered to maintain grid structure - 40% width */}
            <div className="md:col-span-2">
              {activeFilterField && (
                <div className="border border-gray-300 rounded-lg bg-white">
                  {/* Options Header */}
                  <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
                    <span className="text-sm font-semibold text-gray-700">
                      {activeFilterField === 'date' ? 'Select Date Range' : 
                       activeFilterField === 'payment_status' ? 'Select Payment Status' : 
                       `Select ${activeFilterField === 'client' ? 'Clients' : activeFilterField === 'survey_type' ? 'Survey Types' : activeFilterField === 'country' ? 'Countries' : activeFilterField === 'port' ? 'Ports' : 'Surveyors'} (Multiple)`}
                    </span>
                    <button
                      type="button"
                      onClick={() => setActiveFilterField('')}
                      className="text-gray-400 hover:text-gray-600 transition-colors"
                      title="Close"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {/* Options Content */}
                  <div className="max-h-[200px] overflow-y-auto p-3">
                  {activeFilterField === 'date' && (
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                          <svg className="w-4 h-4 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Start Date
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            id="start-date-input"
                            value={displayFilters.start_date}
                            onChange={(e) => {
                              handleDisplayDateChange('start_date', e.target.value)
                            }}
                            disabled={filters.skip_date_filter}
                            placeholder="DD/MM/YYYY"
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                          />
                          <div 
                            className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer z-10"
                            onClick={(e) => {
                              e.stopPropagation()
                              if (!filters.skip_date_filter) {
                                // Create a hidden date input to show the date picker
                                const hiddenInput = document.createElement('input')
                                hiddenInput.type = 'date'
                                hiddenInput.value = filters.start_date
                                
                                // Position the hidden input exactly where the user clicked
                                const rect = e.currentTarget.getBoundingClientRect()
                                const scrollX = window.pageXOffset || document.documentElement.scrollLeft
                                const scrollY = window.pageYOffset || document.documentElement.scrollTop
                                
                                hiddenInput.style.position = 'fixed'
                                hiddenInput.style.left = `${rect.left + scrollX}px`
                                hiddenInput.style.top = `${rect.bottom + scrollY + 2}px`
                                hiddenInput.style.zIndex = '99999'
                                hiddenInput.style.opacity = '0'
                                hiddenInput.style.width = '1px'
                                hiddenInput.style.height = '1px'
                                hiddenInput.style.border = 'none'
                                hiddenInput.style.outline = 'none'
                                
                                document.body.appendChild(hiddenInput)
                                
                                // Focus and show picker immediately
                                hiddenInput.focus()
                                hiddenInput.showPicker?.()
                                
                                // Clean up events
                                const cleanup = () => {
                                  setTimeout(() => {
                                    if (document.body.contains(hiddenInput)) {
                                      document.body.removeChild(hiddenInput)
                                    }
                                  }, 100)
                                }
                                
                                hiddenInput.addEventListener('change', (event) => {
                                  handleFilterChange('start_date', event.target.value)
                                  cleanup()
                                })
                                hiddenInput.addEventListener('cancel', cleanup)
                                hiddenInput.addEventListener('blur', cleanup)
                              }
                            }}
                          >
                            <svg 
                              className="w-5 h-5 text-teal-500 transition-transform hover:scale-110" 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
                          <svg className="w-4 h-4 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          End Date
                        </label>
                        <div className="relative">
                          <input
                            type="text"
                            id="end-date-input"
                            value={displayFilters.end_date}
                            onChange={(e) => {
                              handleDisplayDateChange('end_date', e.target.value)
                            }}
                            disabled={filters.skip_date_filter}
                            placeholder="DD/MM/YYYY"
                            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all disabled:bg-gray-100 disabled:cursor-not-allowed text-sm"
                          />
                          <div 
                            className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer z-10"
                            onClick={(e) => {
                              e.stopPropagation()
                              if (!filters.skip_date_filter) {
                                // Create a hidden date input to show the date picker
                                const hiddenInput = document.createElement('input')
                                hiddenInput.type = 'date'
                                hiddenInput.value = filters.end_date
                                
                                // Position the hidden input exactly where the user clicked
                                const rect = e.currentTarget.getBoundingClientRect()
                                const scrollX = window.pageXOffset || document.documentElement.scrollLeft
                                const scrollY = window.pageYOffset || document.documentElement.scrollTop
                                
                                hiddenInput.style.position = 'fixed'
                                hiddenInput.style.left = `${rect.left + scrollX}px`
                                hiddenInput.style.top = `${rect.bottom + scrollY + 2}px`
                                hiddenInput.style.zIndex = '99999'
                                hiddenInput.style.opacity = '0'
                                hiddenInput.style.width = '1px'
                                hiddenInput.style.height = '1px'
                                hiddenInput.style.border = 'none'
                                hiddenInput.style.outline = 'none'
                                
                                document.body.appendChild(hiddenInput)
                                
                                // Focus and show picker immediately
                                hiddenInput.focus()
                                hiddenInput.showPicker?.()
                                
                                // Clean up events
                                const cleanup = () => {
                                  setTimeout(() => {
                                    if (document.body.contains(hiddenInput)) {
                                      document.body.removeChild(hiddenInput)
                                    }
                                  }, 100)
                                }
                                
                                hiddenInput.addEventListener('change', (event) => {
                                  handleFilterChange('end_date', event.target.value)
                                  cleanup()
                                })
                                hiddenInput.addEventListener('cancel', cleanup)
                                hiddenInput.addEventListener('blur', cleanup)
                              }
                            }}
                          >
                            <svg 
                              className="w-5 h-5 text-teal-500 transition-transform hover:scale-110" 
                              fill="none" 
                              stroke="currentColor" 
                              viewBox="0 0 24 24"
                            >
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                  {activeFilterField === 'client' && (
                    <div className="space-y-1">
                      {filterOptions.clients.length > 0 ? (
                        filterOptions.clients.map(client => (
                          <label key={client} className="flex items-center gap-2 p-2 hover:bg-teal-50 rounded-lg cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={tempSelections.client.includes(client)}
                              onChange={() => toggleTempSelection('client', client)}
                              className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500"
                            />
                            <span className="text-sm text-gray-700">{client}</span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 py-2">No clients available</p>
                      )}
                    </div>
                  )}
                  {activeFilterField === 'survey_type' && (
                    <div className="space-y-1">
                      {filterOptions.surveyTypes.length > 0 ? (
                        filterOptions.surveyTypes.map(type => (
                          <label key={type} className="flex items-center gap-2 p-2 hover:bg-teal-50 rounded-lg cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={tempSelections.survey_type.includes(type)}
                              onChange={() => toggleTempSelection('survey_type', type)}
                              className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500"
                            />
                            <span className="text-sm text-gray-700">{type}</span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 py-2">No survey types available</p>
                      )}
                    </div>
                  )}
                  {activeFilterField === 'country' && (
                    <div className="space-y-1">
                      {filterOptions.countries.length > 0 ? (
                        filterOptions.countries.map(country => (
                          <label key={country} className="flex items-center gap-2 p-2 hover:bg-teal-50 rounded-lg cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={tempSelections.country.includes(country)}
                              onChange={() => toggleTempSelection('country', country)}
                              className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500"
                            />
                            <span className="text-sm text-gray-700">{country}</span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 py-2">No countries available</p>
                      )}
                    </div>
                  )}
                  {activeFilterField === 'port' && (
                    <div className="space-y-1">
                      {filters.country ? (
                        filterOptions.ports.length > 0 ? (
                          <>
                            {filters.country.includes(',') && (
                              <p className="text-xs text-teal-600 mb-2 px-2">
                                Note: Ports shown for first selected country
                              </p>
                            )}
                            {filterOptions.ports.map(port => (
                              <label key={port} className="flex items-center gap-2 p-2 hover:bg-teal-50 rounded-lg cursor-pointer transition-colors">
                                <input
                                  type="checkbox"
                                  checked={tempSelections.port.includes(port)}
                                  onChange={() => toggleTempSelection('port', port)}
                                  className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500"
                                />
                                <span className="text-sm text-gray-700">{port}</span>
                              </label>
                            ))}
                          </>
                        ) : (
                          <p className="text-sm text-gray-500 py-2">No ports available for selected country</p>
                        )
                      ) : (
                        <p className="text-sm text-gray-500 py-2">Please select a country first</p>
                      )}
                    </div>
                  )}
                  {activeFilterField === 'surveyor' && !isReadOnlyClient && (
                    <div className="space-y-1">
                      {filterOptions.surveyors.length > 0 ? (
                        filterOptions.surveyors.map(surveyor => (
                          <label key={surveyor} className="flex items-center gap-2 p-2 hover:bg-teal-50 rounded-lg cursor-pointer transition-colors">
                            <input
                              type="checkbox"
                              checked={tempSelections.surveyor.includes(surveyor)}
                              onChange={() => toggleTempSelection('surveyor', surveyor)}
                              className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500"
                            />
                            <span className="text-sm text-gray-700">{surveyor}</span>
                          </label>
                        ))
                      ) : (
                        <p className="text-sm text-gray-500 py-2">No surveyors available</p>
                      )}
                    </div>
                  )}
                  {activeFilterField === 'payment_status' && (
                    <div className="space-y-1">
                      <label className="flex items-center gap-2 p-2 hover:bg-teal-50 rounded-lg cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={tempSelections.payment_status.includes('Unpaid')}
                          onChange={() => toggleTempSelection('payment_status', 'Unpaid')}
                          className="w-4 h-4 text-teal-500 border-gray-300 rounded focus:ring-teal-500"
                        />
                        <span className="text-sm text-gray-700">💰 Unpaid (No Received Payment Date)</span>
                      </label>
                    </div>
                  )}
                  </div>
                  {/* Add to Filter Button - Only show for fields that need it */}
                  {activeFilterField && activeFilterField !== 'date' && (
                    <div className="p-3 border-t border-gray-200 bg-gray-50">
                      <button
                        type="button"
                        onClick={handleAddToFilter}
                        className="w-full px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                        Add to Filter
                      </button>
                    </div>
                  )}
                  {/* Apply Date Range Button - Only show for date field */}
                  {activeFilterField === 'date' && (
                    <div className="p-3 border-t border-gray-200 bg-gray-50">
                      <button
                        type="button"
                        onClick={() => setActiveFilterField('')}
                        className="w-full px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-200 flex items-center justify-center gap-2"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Apply Date Range
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Column 3: Quick Date Filters - Dropdown - Always in last position - 20% width */}
            <div className="md:col-span-1">
              <label className="block text-sm font-semibold text-gray-700 mb-2">Quick Date Filters</label>
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value) {
                    handleQuickDateFilter(e.target.value)
                    e.target.value = '' // Reset dropdown after selection
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500 transition-all bg-white text-sm"
              >
                <option value="">📅 Select Quick Date Filter</option>
                <option value="all">📅 All</option>
                <option value="this-month">🗓️ This Month</option>
                <option value="this-quarter">📈 This Quarter</option>
                <option value="this-year">📉 This Year</option>
                <option value="last-7-days">⏱️ Last 7 Days</option>
                <option value="last-30-days">⏰ Last 30 Days</option>
                <option value="last-3-months">📅 Last 3 Months</option>
                <option value="last-year">📆 Last Year</option>
              </select>
            </div>

          </div>

          {/* Footer: Selected Filters (Left) and Action Buttons (Right) */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 pt-4 border-t border-gray-200">
            {/* Selected Filters - Left side, single line */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-700 whitespace-nowrap">Selected Filters:</span>
                <div className="flex items-center gap-2 flex-wrap">
                  {selectedFilters.client.map(client => (
                    <span key={client} className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium whitespace-nowrap">
                      👤 {client}
                      <button
                        type="button"
                        onClick={() => toggleFilterOption('client', client)}
                        className="hover:text-orange-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {selectedFilters.survey_type.map(type => (
                    <span key={type} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium whitespace-nowrap">
                      📋 {type}
                      <button
                        type="button"
                        onClick={() => toggleFilterOption('survey_type', type)}
                        className="hover:text-blue-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {selectedFilters.country.map(country => (
                    <span key={country} className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium whitespace-nowrap">
                      🌍 {country}
                      <button
                        type="button"
                        onClick={() => toggleFilterOption('country', country)}
                        className="hover:text-green-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {selectedFilters.port.map(port => (
                    <span key={port} className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-medium whitespace-nowrap">
                      ⚓ {port}
                      <button
                        type="button"
                        onClick={() => toggleFilterOption('port', port)}
                        className="hover:text-purple-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {!isReadOnlyClient && selectedFilters.surveyor.map(surveyor => (
                    <span key={surveyor} className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-medium whitespace-nowrap">
                      👨‍💼 {surveyor}
                      <button
                        type="button"
                        onClick={() => toggleFilterOption('surveyor', surveyor)}
                        className="hover:text-indigo-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {selectedFilters.payment_status.map(status => (
                    <span key={status} className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs font-medium whitespace-nowrap">
                      💰 {status}
                      <button
                        type="button"
                        onClick={() => toggleFilterOption('payment_status', status)}
                        className="hover:text-red-900"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  {(filters.start_date || filters.end_date) && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-teal-100 text-teal-700 rounded-full text-xs font-medium whitespace-nowrap">
                      📅 {filters.start_date || '...'} to {filters.end_date || '...'}
                      <button
                        type="button"
                        onClick={() => {
                          handleFilterChange('start_date', '')
                          handleFilterChange('end_date', '')
                        }}
                        className="hover:text-orange-900"
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {selectedFilters.client.length === 0 && selectedFilters.survey_type.length === 0 && 
                   selectedFilters.country.length === 0 && selectedFilters.port.length === 0 && 
                   selectedFilters.surveyor.length === 0 && selectedFilters.payment_status.length === 0 && 
                   !filters.start_date && !filters.end_date && (
                    <span className="text-xs text-gray-400 italic">No filters selected</span>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons - Right end */}
            <div className="flex flex-wrap gap-2 flex-shrink-0">
              <button
                type="button"
                onClick={handleClearFilters}
                className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 font-semibold rounded-lg transition-colors flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
                Clear All
              </button>
              {!isReadOnlyClient && (
                <button
                  type="button"
                  onClick={handleGenerateSOA}
                  className="px-4 py-2 text-sm bg-white border border-[#d9e2f1] text-[#0a5fe0] hover:border-[#0a5fe0] hover:bg-[#f4f7fc] font-semibold rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  SoA
                </button>
              )}
              {!isReadOnlyClient && (
                <button
                  type="button"
                  onClick={handleExportExcel}
                  className="px-4 py-2 text-sm bg-white border border-[#d9e2f1] text-[#0a5fe0] hover:border-[#0a5fe0] hover:bg-[#f4f7fc] font-semibold rounded-lg transition-colors flex items-center gap-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export Excel
                </button>
              )}
              <button
                type="submit"
                className="px-4 py-2 text-sm bg-premium text-white font-semibold rounded-lg shadow-[0_2px_8px_rgba(1,109,247,0.3)] hover:shadow-[0_4px_14px_rgba(1,109,247,0.4)] transition-shadow flex items-center gap-2"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Apply Filters
              </button>
            </div>
          </div>
        </form>
        </div>
      )}

      {/* Stats Cards */}
      {(() => {
        const uniqueClients = stats?.surveys_by_client ? new Set(stats.surveys_by_client.map(c => c.client_name)).size : 0
        const countries = stats?.surveys_by_country ? new Set(stats.surveys_by_country.map(c => c.survey_country)).size : 0
        const showFinancials = isReadOnlyClient || hasFullAccess
        const statCards = [
          { icon: 'bi-building', label: isReadOnlyClient ? 'Your Team' : 'Unique Clients', value: uniqueClients },
          !isReadOnlyClient && { icon: 'bi-people', label: 'Active Surveyors', value: stats?.surveys_by_surveyor?.length || 0 },
          { icon: 'bi-clipboard-data', label: 'Total Surveys', value: stats?.total_surveys || 0 },
          { icon: 'bi-globe', label: isReadOnlyClient ? 'Your Total Countries' : 'Countries Covered', value: countries },
          showFinancials && {
            icon: 'bi-fuel-pump',
            label: 'Total Recovered Fuel (MT)',
            value: stats?.total_recovered_fuel_mt ? parseFloat(stats.total_recovered_fuel_mt).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '0.000',
          },
          showFinancials && {
            icon: 'bi-cash-stack',
            label: 'Total Saving',
            value: `$${stats?.total_saving ? parseFloat(stats.total_saving).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}`,
          },
        ].filter(Boolean)
        return (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
            {statCards.map((card) => (
              <StatCard key={card.label} icon={card.icon} label={card.label} value={card.value} />
            ))}
          </div>
        )
      })()}

      {/* Analytics Row */}
      <div className="mb-8">
        <h2 className="mb-4 flex items-center gap-2.5 text-xl font-semibold">
          <i className="bi bi-graph-up-arrow icon-premium"></i>
          <span className="text-premium">Analytics</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Survey Trends Card */}
          <div className="card-premium relative overflow-hidden p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="bi bi-graph-up icon-premium"></i>
                  <span className="truncate max-w-[25ch]">Surveys</span>
                </div>
                <div className="text-2xl font-semibold tabular-nums text-premium">
                  {trendData.reduce((sum, d) => sum + d.count, 0)}
                </div>
              </div>
            </h2>
            {trendData.length > 0 ? (
              <div className="h-[200px] relative">
                <Line
                  data={{
                    labels: trendData.map(d => d.month),
                    datasets: [{
                      label: 'Number of Surveys',
                      data: trendData.map(d => d.count),
                      borderColor: 'rgb(1, 109, 247)',
                      backgroundColor: 'rgba(1, 109, 247, 0.12)',
                      tension: 0.4,
                      fill: true,
                      borderWidth: 2,
                      pointRadius: 6,
                      pointHoverRadius: 8,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: 'rgba(0, 0, 0, 0.05)',
                        },
                      },
                      x: {
                        grid: {
                          display: false,
                        },
                      },
                    },
                  }}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-center text-sm text-slate-400">No data available</p>
              </div>
            )}
          </div>

          {/* Recovered Fuel Card */}
          <div className="card-premium relative overflow-hidden p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="bi bi-fuel-pump icon-premium"></i>
                  <span className="truncate max-w-[25ch]">Recovered Fuel</span>
                </div>
                <div className="text-2xl font-semibold tabular-nums text-premium">
                  {stats?.total_recovered_fuel_mt ? parseFloat(stats.total_recovered_fuel_mt).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '0.000'}
                </div>
              </div>
            </h2>
            {fuelTrendData.length > 0 && fuelTrendData.some(d => d.value > 0) ? (
              <div className="h-[200px] relative">
                <Line
                  data={{
                    labels: fuelTrendData.map(d => d.month),
                    datasets: [{
                      label: 'Recovered Fuel (MT)',
                      data: fuelTrendData.map(d => d.value),
                      borderColor: 'rgb(1, 109, 247)',
                      backgroundColor: 'rgba(1, 109, 247, 0.12)',
                      tension: 0.4,
                      fill: true,
                      borderWidth: 2,
                      pointRadius: 6,
                      pointHoverRadius: 8,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: 'rgba(0, 0, 0, 0.05)',
                        },
                        ticks: {
                          callback: function(value) {
                            return value.toFixed(1) + ' MT'
                          }
                        }
                      },
                      x: {
                        grid: {
                          display: false,
                        },
                      },
                    },
                  }}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px]">
                <div className="text-2xl font-semibold tabular-nums mb-1 text-slate-900">
                  {stats?.total_recovered_fuel_mt ? parseFloat(stats.total_recovered_fuel_mt).toLocaleString('en-US', { minimumFractionDigits: 3, maximumFractionDigits: 3 }) : '0.000'}
                </div>
                <div className="text-sm font-medium text-slate-500">Total Metric Tons</div>
              </div>
            )}
          </div>

          {/* Saving Card */}
          <div className="card-premium relative overflow-hidden p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="bi bi-cash-stack icon-premium"></i>
                  <span className="truncate max-w-[25ch]">Saving</span>
                </div>
                <div className="text-2xl font-semibold tabular-nums text-premium">
                  ${stats?.total_saving ? parseFloat(stats.total_saving).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }) : '0'}
                </div>
              </div>
            </h2>
            {savingTrendData.length > 0 && savingTrendData.some(d => d.value > 0) ? (
              <div className="h-[200px] relative">
                <Line
                  data={{
                    labels: savingTrendData.map(d => d.month),
                    datasets: [{
                      label: 'Savings ($)',
                      data: savingTrendData.map(d => d.value),
                      borderColor: 'rgb(1, 109, 247)',
                      backgroundColor: 'rgba(1, 109, 247, 0.12)',
                      tension: 0.4,
                      fill: true,
                      borderWidth: 2,
                      pointRadius: 6,
                      pointHoverRadius: 8,
                    }],
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: { display: false },
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: 'rgba(0, 0, 0, 0.05)',
                        },
                        ticks: {
                          callback: function(value) {
                            return '$' + (value / 1000).toFixed(0) + 'K'
                          }
                        }
                      },
                      x: {
                        grid: {
                          display: false,
                        },
                      },
                    },
                  }}
                />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-[200px]">
                <div className="text-2xl font-semibold tabular-nums mb-1 text-slate-900">
                  ${stats?.total_saving ? parseFloat(stats.total_saving).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                </div>
                <div className="text-sm font-medium text-slate-500">Total Savings</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Donut Charts Row */}
      <div className="mb-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Survey Types Donut Chart */}
          <div className="card-premium relative overflow-hidden p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="bi bi-pie-chart icon-premium"></i>
                  <span>Survey Types</span>
                </div>
                <div className="text-2xl font-semibold tabular-nums text-premium">
                  {typeData.length}
                </div>
              </div>
            </h2>
            {typeData.length > 0 ? (
              <div className="h-[200px] flex items-center">
                <div className="w-1/2 relative max-h-[180px] flex items-center justify-center p-1">
                  <div className="w-full h-full">
                    <Doughnut
                    data={{
                      labels: typeData.map(d => d.survey_type),
                      datasets: [{
                        data: typeData.map(d => d.count),
                        backgroundColor: [
                          'rgba(59, 130, 246, 0.85)',
                          'rgba(16, 185, 129, 0.85)',
                          'rgba(251, 146, 60, 0.85)',
                          'rgba(239, 68, 68, 0.85)',
                          'rgba(168, 85, 247, 0.85)',
                          'rgba(236, 72, 153, 0.85)',
                          'rgba(139, 92, 246, 0.85)',
                          'rgba(34, 197, 94, 0.85)',
                        ],
                        borderColor: '#ffffff',
                        borderWidth: 2,
                        hoverBorderWidth: 3,
                      }],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: '65%',
                      plugins: {
                        legend: { display: false },
                      },
                    }}
                  />
                  </div>
                </div>
                <div className="w-1/2 max-h-[180px] overflow-y-auto pl-4">
                  <div className="space-y-1 text-xs">
                    {typeData.map((item, index) => {
                      const total = typeData.reduce((sum, d) => sum + d.count, 0);
                      const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                      return (
                        <div key={index} className="flex items-center gap-2 justify-end">
                          <div className="flex-1 min-w-0 text-right">
                            <div className="font-medium truncate max-w-[35ch]" style={{ color: '#374151' }}>
                              {item.survey_type}
                            </div>
                            <div className="text-gray-500 whitespace-nowrap">
                              ({percentage}%)
                            </div>
                          </div>
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5 -ml-1" 
                            style={{ backgroundColor: [
                              'rgba(59, 130, 246, 0.85)',
                              'rgba(16, 185, 129, 0.85)',
                              'rgba(251, 146, 60, 0.85)',
                              'rgba(239, 68, 68, 0.85)',
                              'rgba(168, 85, 247, 0.85)',
                              'rgba(236, 72, 153, 0.85)',
                              'rgba(139, 92, 246, 0.85)',
                              'rgba(34, 197, 94, 0.85)',
                            ][index] }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-center text-sm text-slate-400">No data available</p>
              </div>
            )}
          </div>

          {/* Countries Donut Chart */}
          <div className="card-premium relative overflow-hidden p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="bi bi-globe icon-premium"></i>
                  <span>Countries</span>
                </div>
                <div className="text-2xl font-semibold tabular-nums text-premium">
                  {stats?.surveys_by_country ? stats.surveys_by_country.length : 0}
                </div>
              </div>
            </h2>
            {stats?.surveys_by_country && stats.surveys_by_country.length > 0 ? (
              <div className="h-[200px] flex items-center">
                <div className="w-1/2 relative max-h-[180px] flex items-center justify-center p-1">
                  <div className="w-full h-full">
                    <Pie
                    data={{
                      labels: stats.surveys_by_country.map(c => c.survey_country),
                      datasets: [{
                        data: stats.surveys_by_country.map(c => c.count),
                        backgroundColor: [
                          'rgba(59, 130, 246, 0.85)',
                          'rgba(16, 185, 129, 0.85)',
                          'rgba(251, 146, 60, 0.85)',
                          'rgba(239, 68, 68, 0.85)',
                          'rgba(168, 85, 247, 0.85)',
                          'rgba(236, 72, 153, 0.85)',
                          'rgba(139, 92, 246, 0.85)',
                          'rgba(34, 197, 94, 0.85)',
                        ],
                        borderColor: '#ffffff',
                        borderWidth: 2,
                        hoverBorderWidth: 3,
                      }],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                      },
                    }}
                  />
                  </div>
                </div>
                <div className="w-1/2 max-h-[180px] overflow-y-auto pl-4">
                  <div className="space-y-1 text-xs">
                    {stats.surveys_by_country.map((item, index) => {
                      const total = stats.surveys_by_country.reduce((sum, c) => sum + c.count, 0);
                      const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                      return (
                        <div key={index} className="flex items-center gap-2 justify-end">
                          <div className="flex-1 min-w-0 text-right">
                            <div className="font-medium truncate max-w-[35ch]" style={{ color: '#374151' }}>
                              {item.survey_country}
                            </div>
                            <div className="text-gray-500 whitespace-nowrap">
                              ({percentage}%)
                            </div>
                          </div>
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5 -ml-1" 
                            style={{ backgroundColor: [
                              'rgba(59, 130, 246, 0.85)',
                              'rgba(16, 185, 129, 0.85)',
                              'rgba(251, 146, 60, 0.85)',
                              'rgba(239, 68, 68, 0.85)',
                              'rgba(168, 85, 247, 0.85)',
                              'rgba(236, 72, 153, 0.85)',
                              'rgba(139, 92, 246, 0.85)',
                              'rgba(34, 197, 94, 0.85)',
                            ][index] }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-center text-sm text-slate-400">No data available</p>
              </div>
            )}
          </div>

          {/* Clients Donut Chart */}
          <div className="card-premium relative overflow-hidden p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <i className="bi bi-building icon-premium"></i>
                  <span>Clients</span>
                </div>
                <div className="text-2xl font-semibold tabular-nums text-premium">
                  {stats?.surveys_by_client ? stats.surveys_by_client.length : 0}
                </div>
              </div>
            </h2>
            {stats?.surveys_by_client && stats.surveys_by_client.length > 0 ? (
              <div className="h-[200px] flex items-center">
                <div className="w-1/2 relative max-h-[180px] flex items-center justify-center p-1">
                  <div className="w-full h-full">
                    <Doughnut
                    data={{
                      labels: stats.surveys_by_client.map(c => c.client_name),
                      datasets: [{
                        data: stats.surveys_by_client.map(c => c.count),
                        backgroundColor: [
                          'rgba(59, 130, 246, 0.85)',
                          'rgba(16, 185, 129, 0.85)',
                          'rgba(251, 146, 60, 0.85)',
                          'rgba(239, 68, 68, 0.85)',
                          'rgba(168, 85, 247, 0.85)',
                          'rgba(236, 72, 153, 0.85)',
                          'rgba(139, 92, 246, 0.85)',
                          'rgba(34, 197, 94, 0.85)',
                        ],
                        borderColor: '#ffffff',
                        borderWidth: 2,
                        hoverBorderWidth: 3,
                      }],
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      cutout: '65%',
                      plugins: {
                        legend: { display: false },
                      },
                    }}
                  />
                  </div>
                </div>
                <div className="w-1/2 max-h-[180px] overflow-y-auto pl-4">
                  <div className="space-y-1 text-xs">
                    {stats.surveys_by_client.map((item, index) => {
                      const total = stats.surveys_by_client.reduce((sum, c) => sum + c.count, 0);
                      const percentage = total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                      return (
                        <div key={index} className="flex items-center gap-2 justify-end">
                          <div className="flex-1 min-w-0 text-right">
                            <div className="font-medium truncate max-w-[35ch]" style={{ color: '#374151' }}>
                              {item.client_name}
                            </div>
                            <div className="text-gray-500 whitespace-nowrap">
                              ({percentage}%)
                            </div>
                          </div>
                          <div 
                            className="w-3 h-3 rounded-full flex-shrink-0 mt-0.5 -ml-1" 
                            style={{ backgroundColor: [
                              'rgba(59, 130, 246, 0.85)',
                              'rgba(16, 185, 129, 0.85)',
                              'rgba(251, 146, 60, 0.85)',
                              'rgba(239, 68, 68, 0.85)',
                              'rgba(168, 85, 247, 0.85)',
                              'rgba(236, 72, 153, 0.85)',
                              'rgba(139, 92, 246, 0.85)',
                              'rgba(34, 197, 94, 0.85)',
                            ][index] }}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-center text-sm text-slate-400">No data available</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Clients Table - Hidden for clientusers (they only see their own data) */}
        {(
          <div className="card-premium p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="bi bi-building icon-premium"></i>
                Clients
              </div>
            </h2>
            {stats?.surveys_by_client && stats.surveys_by_client.length > 0 ? (
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full">
                  <thead className="table-head-premium sticky top-0 z-10">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Client</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Surveys</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Distribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.surveys_by_client.map((item, index) => {
                      const total = stats.surveys_by_client.reduce((sum, i) => sum + i.count, 0)
                      const percentage = Math.round((item.count / total) * 100)
                      const colors = [
                        'bg-blue-500',
                        'bg-green-500',
                        'bg-yellow-500',
                        'bg-red-500',
                        'bg-purple-500',
                        'bg-pink-500',
                      ]
                      return (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4 text-sm text-gray-700">{item.client_name || 'N/A'}</td>
                          <td className="py-3 px-4 text-sm text-gray-700 text-right font-medium">{item.count}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${colors[index % colors.length]}`}
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-500 w-10 text-right">{percentage}%</span>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-center text-sm text-slate-400">No data available</p>
              </div>
            )}
          </div>
        )}

        {/* Top Surveyors Table - Hidden for ReadOnlyClient users */}
        {!isReadOnlyClient && (
          <div className="card-premium p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="bi bi-person-badge icon-premium"></i>
                Surveyors
              </div>
            </h2>
            {stats?.surveys_by_surveyor && stats.surveys_by_surveyor.length > 0 ? (
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full">
                  <thead className="table-head-premium sticky top-0 z-10">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Surveyor</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Surveys</th>
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Distribution</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.surveys_by_surveyor.map((item, index) => {
                      const total = stats.surveys_by_surveyor.reduce((sum, i) => sum + i.count, 0)
                      const percentage = Math.round((item.count / total) * 100)
                      const colors = [
                        'bg-blue-500',
                        'bg-green-500',
                        'bg-yellow-500',
                        'bg-red-500',
                        'bg-purple-500',
                        'bg-pink-500',
                      ]
                      return (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                          <td className="py-3 px-4 text-sm text-gray-700">{item.surveyor || 'N/A'}</td>
                          <td className="py-3 px-4 text-sm text-gray-700 text-right font-medium">{item.count}</td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${colors[index % colors.length]}`}
                                  style={{ width: `${percentage}%` }}
                                ></div>
                              </div>
                              <span className="text-xs text-gray-500 w-10 text-right">{percentage}%</span>
                            </div>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-center text-sm text-slate-400">No data available</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Clients by Fuel & Savings Analysis - Visible for superusers, finance managers and clientusers */}
      {hasFullAccess && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Clients by Total Recovered Fuel */}
          <div className="card-premium p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="bi bi-fuel-pump icon-premium"></i>
                Total Recovered Fuel
              </div>
            </h2>
            {stats?.clients_by_fuel && stats.clients_by_fuel.length > 0 ? (
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full">
                  <thead className="table-head-premium sticky top-0 z-10">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Client</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Total Fuel (MT)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.clients_by_fuel.map((item, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 text-sm text-gray-700">{item.client_name || 'N/A'}</td>
                        <td className="py-3 px-4 text-sm text-gray-700 text-right font-medium">
                          {parseFloat(item.total_fuel || 0).toFixed(3)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-center text-sm text-slate-400">No data available</p>
              </div>
            )}
          </div>

          {/* Clients by Total Saving */}
          <div className="card-premium p-5">
            <h2 className="text-base font-semibold mb-4 text-slate-900 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <i className="bi bi-cash-stack icon-premium"></i>
                Total Saving
              </div>
            </h2>
            {stats?.clients_by_saving && stats.clients_by_saving.length > 0 ? (
              <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
                <table className="w-full">
                  <thead className="table-head-premium sticky top-0 z-10">
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Client</th>
                      <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Total Saving ($)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.clients_by_saving.map((item, index) => (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 text-sm text-gray-700">{item.client_name || 'N/A'}</td>
                        <td className="py-3 px-4 text-sm text-gray-700 text-right font-medium">
                          ${parseFloat(item.total_savings || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex items-center justify-center h-[200px]">
                <p className="text-center text-sm text-slate-400">No data available</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Locations Tables Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Top Countries Table */}
        <div className="card-premium p-5">
          <h2 className="text-base font-semibold mb-4 text-slate-900 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="bi bi-globe icon-premium"></i>
              Countries
            </div>
          </h2>
          {stats?.surveys_by_country && stats.surveys_by_country.length > 0 ? (
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full">
                <thead className="table-head-premium sticky top-0 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Country</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Surveys</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.surveys_by_country.map((item, index) => {
                    const total = stats.surveys_by_country.reduce((sum, i) => sum + i.count, 0)
                    const percentage = Math.round((item.count / total) * 100)
                    const colors = [
                      'bg-blue-500',
                      'bg-green-500',
                      'bg-yellow-500',
                      'bg-red-500',
                      'bg-purple-500',
                      'bg-pink-500',
                    ]
                    return (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 text-sm text-gray-700">{item.survey_country || 'N/A'}</td>
                        <td className="py-3 px-4 text-sm text-gray-700 text-right font-medium">{item.count}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${colors[index % colors.length]}`}
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-500 w-10 text-right">{percentage}%</span>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[200px]">
              <p className="text-center text-sm text-slate-400">No data available</p>
            </div>
          )}
        </div>

        {/* Top Ports Table */}
        <div className="card-premium p-5">
          <h2 className="text-base font-semibold mb-4 text-slate-900 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <i className="bi bi-water icon-premium"></i>
              Ports
            </div>
          </h2>
          {stats?.surveys_by_port && stats.surveys_by_port.length > 0 ? (
            <div className="overflow-x-auto max-h-[400px] overflow-y-auto">
              <table className="w-full">
                <thead className="table-head-premium sticky top-0 z-10">
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Port</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Surveys</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Distribution</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.surveys_by_port.map((item, index) => {
                    const total = stats.surveys_by_port.reduce((sum, i) => sum + i.count, 0)
                    const percentage = Math.round((item.count / total) * 100)
                    const colors = [
                      'bg-blue-500',
                      'bg-green-500',
                      'bg-yellow-500',
                      'bg-red-500',
                      'bg-purple-500',
                      'bg-pink-500',
                    ]
                    return (
                      <tr key={index} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                        <td className="py-3 px-4 text-sm text-gray-700">{item.survey_port || 'N/A'}</td>
                        <td className="py-3 px-4 text-sm text-gray-700 text-right font-medium">{item.count}</td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <div className="flex-1 bg-gray-200 rounded-full h-2">
                              <div
                                className={`h-2 rounded-full ${colors[index % colors.length]}`}
                                style={{ width: `${percentage}%` }}
                              ></div>
                            </div>
                            <span className="text-xs text-gray-500 w-10 text-right">{percentage}%</span>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="flex items-center justify-center h-[200px]">
              <p className="text-center text-sm text-slate-400">No data available</p>
            </div>
          )}
        </div>
      </div>

      {/* Survey List Section */}
      <div className="card-premium p-5 mt-8">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-base font-semibold text-slate-900 flex items-center gap-2">
            <i className="bi bi-list-ul icon-premium"></i>
            Survey List
          </h2>
          {!surveysLoading && (
            <div className="text-sm text-gray-600">
              {stats && (
                <span>
                  Total: {stats.total_surveys || 0} surveys
                  {surveyTotalCount > 0 && surveyTotalPages > 1 && (
                    <span className="ml-2">
                      (Showing page {surveyPage} of {surveyTotalPages})
                    </span>
                  )}
                </span>
              )}
            </div>
          )}
        </div>
        {surveysLoading ? (
          <div className="flex items-center justify-center h-[200px]">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            <p className="text-gray-600 ml-4 text-sm">Loading surveys...</p>
          </div>
        ) : surveys.length === 0 ? (
          <div className="flex items-center justify-center h-[200px]">
            <p className="text-gray-500 text-sm">No surveys found</p>
          </div>
        ) : (
          <>
            {/* Column Headers */}
            <div className="table-head-premium rounded-lg px-4 py-2.5 mb-3 shadow-[0_1px_6px_rgba(1,109,247,0.3)]">
              <div className="grid grid-cols-7 gap-3 text-xs font-semibold uppercase tracking-wide">
                <div className="col-span-1">
                  <span>Client</span>
                </div>
                <div className="col-span-1">
                  <span>Survey Type</span>
                </div>
                <div className="col-span-1">
                  <span>Vessel</span>
                </div>
                <div className="col-span-1">
                  <span>Country</span>
                </div>
                <div className="col-span-1">
                  <span>Port</span>
                </div>
                <div className="col-span-1">
                  <span>Reference Number</span>
                </div>
                <div className="col-span-1 text-right">
                  <span>Report</span>
                </div>
              </div>
            </div>
            <div className="space-y-2 max-h-[600px] overflow-y-auto">
              {surveys.map((survey) => {
                const country = survey.survey_country || '-'
                const referenceNumber = survey.reference_number || '-'

                return (
                  <div
                    key={survey.reference_number || survey.id}
                    className="bg-white transition-all rounded-lg shadow-sm hover:shadow-md border border-gray-200"
                  >
                    <div className="pl-4 pr-6 py-3">
                      <div className="grid grid-cols-7 gap-3 items-center text-sm">
                        <div className="col-span-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-teal-500 text-base flex-shrink-0">👤</span>
                            <span className="text-gray-900 truncate whitespace-nowrap" title={survey.client_name || '-'}>
                              {survey.client_name || '-'}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-teal-500 text-base flex-shrink-0">📋</span>
                            <span className="text-gray-900 truncate whitespace-nowrap" title={survey.survey_type || '-'}>
                              {survey.survey_type || '-'}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-teal-500 text-base flex-shrink-0">🚢</span>
                            <span className="text-gray-900 truncate whitespace-nowrap" title={survey.vessel_name || '-'}>
                              {survey.vessel_name || '-'}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-teal-500 text-base flex-shrink-0">🌍</span>
                            <span className="text-gray-900 truncate whitespace-nowrap" title={country}>
                              {country}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="flex items-center gap-1.5">
                            <span className="text-teal-500 text-base flex-shrink-0">⚓</span>
                            <span className="text-gray-900 truncate whitespace-nowrap" title={survey.survey_port || '-'}>
                              {survey.survey_port || '-'}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-1">
                          <div className="flex items-center gap-1.5">
                            <img 
                              src={`${process.env.PUBLIC_URL || ''}/images/logo_shape.png`}
                              alt="JNJ Logo" 
                              className="w-5 h-5 object-contain flex-shrink-0"
                            />
                            <span className="text-gray-500 truncate whitespace-nowrap" title={referenceNumber}>
                              {referenceNumber}
                            </span>
                          </div>
                        </div>
                        <div className="col-span-1 flex justify-end">
                          <button
                            type="button"
                            onClick={() => survey.has_reports && setReportsModalRef(survey.reference_number)}
                            disabled={!survey.has_reports}
                            className={`w-8 h-8 flex items-center justify-center rounded-md transition-shadow ${
                              survey.has_reports
                                ? 'bg-premium text-white shadow-[0_2px_8px_rgba(1,109,247,0.3)] hover:shadow-[0_4px_14px_rgba(1,109,247,0.4)]'
                                : 'bg-gray-100 text-gray-300 border border-gray-200 cursor-not-allowed'
                            }`}
                            title={survey.has_reports ? "View / download this survey's reports" : 'No reports uploaded yet'}
                            aria-label={survey.has_reports ? 'View reports' : 'No reports'}
                          >
                            <i className="bi bi-folder2-open"></i>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
            
            {/* Pagination */}
            {surveyTotalPages > 1 && (
              <div className="flex justify-center items-center gap-3 mt-6 pt-6 border-t border-gray-200">
                <button
                  onClick={() => {
                    const newPage = Math.max(1, surveyPage - 1)
                    setSurveyPage(newPage)
                    fetchSurveys(newPage)
                  }}
                  disabled={surveyPage === 1}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  <i className="bi bi-chevron-left mr-1"></i>
                  Previous
                </button>
                <span className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg">
                  Page {surveyPage} of {surveyTotalPages}
                </span>
                <button
                  onClick={() => {
                    const newPage = Math.min(surveyTotalPages, surveyPage + 1)
                    setSurveyPage(newPage)
                    fetchSurveys(newPage)
                  }}
                  disabled={surveyPage === surveyTotalPages}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  Next
                  <i className="bi bi-chevron-right ml-1"></i>
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Report download modal - view/download only from the Dashboard; uploading stays on the Survey List */}
      {reportsModalRef && (
        <SurveyReportsModal
          referenceNumber={reportsModalRef}
          canUpload={false}
          onClose={() => setReportsModalRef(null)}
        />
      )}

      {/* SoA Modal */}
      {showSOAModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-7xl w-full max-h-[90vh] overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold text-white flex items-center gap-3">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Statement of Account
              </h2>
              <div className="flex items-center gap-3">
                <button
                  onClick={handleExportSOA}
                  className="px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 text-white font-semibold rounded-lg transition-all duration-200 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Export Excel
                </button>
                <button
                  onClick={() => setShowSOAModal(false)}
                  className="p-2 rounded-lg hover:bg-white/20 transition-all duration-200 hover:scale-110 active:scale-95"
                  title="Close"
                >
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {soaLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
                </div>
              ) : soaData.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-16 h-16 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <p className="text-gray-500 text-lg">No data found for the selected filters</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    {/* Table Header with Column Groups */}
                    <thead className="table-head-premium">
                      <tr className="border-b-2 border-gray-200">
                        {/* Group 1: Billing */}
                        <th colSpan="6" className="px-4 py-3 text-center font-bold text-gray-800 bg-blue-50 border-b-2 border-blue-200">
                          Billing
                        </th>
                        {/* Group 2: Recovery Amount for Client */}
                        <th colSpan="6" className="px-4 py-3 text-center font-bold text-gray-800 bg-green-50 border-b-2 border-green-200">
                          Recovery Amount for Client (LSFO, HSFO, MGO)
                        </th>
                        {/* Group 3: Invoice Amount */}
                        <th colSpan="6" className="px-4 py-3 text-center font-bold text-gray-800 bg-orange-50 border-b-2 border-orange-200">
                          Invoice Amount
                        </th>
                      </tr>
                      <tr className="bg-gray-100 border-b border-gray-300">
                        {/* Billing Columns */}
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-r border-gray-300">Reference No</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-r border-gray-300">Client</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-r border-gray-300">Survey Type</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-r border-gray-300">Attendance Date</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-r border-gray-300">Vessel</th>
                        <th className="px-4 py-2 text-left text-sm font-semibold text-gray-700 border-r border-gray-300">Country / Port</th>
                        {/* Recovery Amount for Client Columns */}
                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700 border-r border-gray-300">LSFO (Diff)</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700 border-r border-gray-300">LSFO (Recovery)</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700 border-r border-gray-300">HSFO (Diff)</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700 border-r border-gray-300">HSFO (Recovery)</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700 border-r border-gray-300">MGO (Diff)</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700 border-r border-gray-300">MGO (Recovery)</th>
                        {/* Invoice Amount Columns */}
                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700 border-r border-gray-300">Incentive</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700 border-r border-gray-300">Survey Fee</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700 border-r border-gray-300">Expenses</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700 border-r border-gray-300">Others</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700 border-r border-gray-300">Discount</th>
                        <th className="px-4 py-2 text-right text-sm font-semibold text-gray-700 border-r border-gray-300">Total</th>
                      </tr>
                    </thead>

                    {/* Table Body */}
                    <tbody>
                      {soaData.map((row, index) => (
                        <tr key={index} className="hover:bg-gray-50 border-b border-gray-200">
                          {/* Billing Data */}
                          <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-200">{row.reference_no || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-200">{row.client || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-200">{row.survey_type || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-200">{row.attendance_date || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-200">{row.vessel || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 border-r border-gray-200">{row.country_port || '-'}</td>
                          {/* Recovery Amount for Client */}
                          <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-200">{row.lsfo ? row.lsfo.toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-200">{row.lsfo_recovery ? row.lsfo_recovery.toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-200">{row.hsfo ? row.hsfo.toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-200">{row.hsfo_recovery ? row.hsfo_recovery.toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-200">{row.mgo ? row.mgo.toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-200">{row.mgo_recovery ? row.mgo_recovery.toLocaleString() : '-'}</td>
                          {/* Invoice Amount */}
                          <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-200">{row.incentive ? row.incentive.toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-200">{row.survey_fee ? row.survey_fee.toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-200">{row.expenses ? row.expenses.toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-200">{row.others ? row.others.toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-sm text-right text-gray-900 border-r border-gray-200">{row.discount ? row.discount.toLocaleString() : '-'}</td>
                          <td className="px-4 py-3 text-sm text-right font-semibold text-gray-900">{row.total ? row.total.toLocaleString() : '-'}</td>
                        </tr>
                      ))}
                      
                      {/* Totals Row */}
                      <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                        <td colSpan="6" className="px-4 py-3 text-sm font-bold text-gray-800 border-r border-gray-300">
                          TOTAL
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-800 border-r border-gray-200">
                          {soaData.reduce((sum, row) => sum + (parseFloat(row.lsfo) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-800 border-r border-gray-200">
                          {soaData.reduce((sum, row) => sum + (parseFloat(row.lsfo_recovery) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-800 border-r border-gray-200">
                          {soaData.reduce((sum, row) => sum + (parseFloat(row.hsfo) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-800 border-r border-gray-200">
                          {soaData.reduce((sum, row) => sum + (parseFloat(row.hsfo_recovery) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-800 border-r border-gray-200">
                          {soaData.reduce((sum, row) => sum + (parseFloat(row.mgo) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-800 border-r border-gray-200">
                          {soaData.reduce((sum, row) => sum + (parseFloat(row.mgo_recovery) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-800 border-r border-gray-200">
                          {soaData.reduce((sum, row) => sum + (parseFloat(row.incentive) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-800 border-r border-gray-200">
                          {soaData.reduce((sum, row) => sum + (parseFloat(row.survey_fee) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-800 border-r border-gray-200">
                          {soaData.reduce((sum, row) => sum + (parseFloat(row.expenses) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-800 border-r border-gray-200">
                          {soaData.reduce((sum, row) => sum + (parseFloat(row.others) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-800 border-r border-gray-200">
                          {soaData.reduce((sum, row) => sum + (parseFloat(row.discount) || 0), 0).toLocaleString()}
                        </td>
                        <td className="px-4 py-3 text-sm text-right font-bold text-gray-800">
                          {soaData.reduce((sum, row) => sum + (parseFloat(row.total) || 0), 0).toLocaleString()}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Dashboard

